/**
 * Fuzzy String Matching Utilities
 * Handles approximate string matching for location queries with spelling mistakes
 */

/**
 * Calculate Levenshtein distance between two strings
 * This measures the minimum number of single-character edits needed to change one string into another
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Edit distance (0 = identical, higher = more different)
 */
function levenshteinDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Create a matrix to store distances
  const matrix = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(null));
  
  // Initialize first row and column
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;
  
  // Fill the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  return matrix[len1][len2];
}

/**
 * Calculate similarity ratio between two strings (0 to 1)
 * 1.0 = identical, 0.0 = completely different
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity ratio between 0 and 1
 */
export function similarityRatio(str1, str2) {
  if (str1 === str2) return 1.0;
  if (!str1 || !str2) return 0.0;
  
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1.0;
  
  const distance = levenshteinDistance(str1, str2);
  return 1 - distance / maxLen;
}

/**
 * Check if a string contains a keyword with fuzzy matching
 * Handles spelling mistakes, concatenated words (e.g., "NearBuy" vs "near by"), and typos
 * 
 * @param {string} text - The text to search in
 * @param {string} keyword - The keyword to find
 * @param {number} threshold - Minimum similarity ratio (0-1). Default: 0.7
 * @returns {boolean} - True if keyword is found with sufficient similarity
 */
function fuzzyContains(text, keyword, threshold = 0.7) {
  if (!text || !keyword) return false;
  
  const textLower = text.toLowerCase();
  const keywordLower = keyword.toLowerCase();
  
  // First check exact match (fast path)
  if (textLower.includes(keywordLower)) {
    return true;
  }
  
  // Handle concatenated words: split on capital letters or word boundaries
  // This helps with cases like "NearBuy" (one word) matching "near by" (two words)
  const textWords = textLower.split(/\s+|(?=[a-z][A-Z])/).filter(w => w.length > 0);
  const keywordWords = keywordLower.split(/\s+/);
  
  // For single-word keywords, check against text and all text words
  if (keywordWords.length === 1) {
    const singleKeyword = keywordWords[0];
    
    // Check exact match in text
    if (textLower.includes(singleKeyword)) {
      return true;
    }
    
    // Check against each word in text (handles concatenated words)
    for (const textWord of textWords) {
      const similarity = similarityRatio(textWord, singleKeyword);
      if (similarity >= threshold) {
        return true;
      }
    }
    
    // Check substrings in text (for partial matches)
    const keywordLen = singleKeyword.length;
    for (let i = 0; i <= textLower.length - keywordLen + 2; i++) {
      for (let len = keywordLen - 1; len <= keywordLen + 1; len++) {
        if (len < 1 || i + len > textLower.length) continue;
        
        const substring = textLower.substring(i, i + len);
        const similarity = similarityRatio(substring, singleKeyword);
        
        if (similarity >= threshold) {
          return true;
        }
      }
    }
  } else {
    // For multi-word keywords (e.g., "near by", "near me")
    // Check if all keyword words have a fuzzy match in text words
    let matchedWords = 0;
    for (const kw of keywordWords) {
      // Check against individual words
      for (const tw of textWords) {
        if (similarityRatio(tw, kw) >= threshold) {
          matchedWords++;
          break;
        }
      }
      
      // Also check if keyword appears as substring in any text word
      // (handles "NearBuy" containing "near" and "buy" separately)
      if (matchedWords === 0 || !textWords.some(tw => similarityRatio(tw, kw) >= threshold)) {
        // Check if keyword is a substring of any text word
        for (const tw of textWords) {
          if (tw.includes(kw) || similarityRatio(tw, kw) >= threshold) {
            matchedWords++;
            break;
          }
        }
      }
    }
    
    // If most words match, consider it a match
    return matchedWords >= Math.ceil(keywordWords.length * 0.7);
  }
  
  return false;
}

/**
 * Check if text contains any of the keywords with fuzzy matching
 * 
 * @param {string} text - The text to search in
 * @param {string[]} keywords - Array of keywords to check
 * @param {number} threshold - Minimum similarity ratio (0-1). Default: 0.7
 * @returns {{match: boolean, matchedKeyword: string|null}} - Match result with matched keyword
 */
export function fuzzyMatchAny(text, keywords, threshold = 0.7) {
  if (!text || !keywords || keywords.length === 0) {
    return { match: false, matchedKeyword: null };
  }
  
  for (const keyword of keywords) {
    if (fuzzyContains(text, keyword, threshold)) {
      return { match: true, matchedKeyword: keyword };
    }
  }
  
  return { match: false, matchedKeyword: null };
}

/**
 * Check if text is likely a location query using fuzzy matching
 * This is a specialized function for location-related queries
 * Handles spelling mistakes, concatenated words (e.g., "NearBuy" vs "nearby"), and typos
 * 
 * @param {string} text - The text to check
 * @param {number} threshold - Minimum similarity ratio (0-1). Default: 0.75
 * @returns {boolean} - True if text appears to be a location query
 */
export function isFuzzyLocationQuery(text, threshold = 0.75) {
  if (!text || typeof text !== 'string') {
    return false;
  }
  
  const normalizedText = text.trim().toLowerCase();
  
  // Location keywords that should trigger location permission request
  const locationKeywords = [
    'near me',
    'nearby',
    'near by',
    'nearest',
    'closest',
    'around me',
    'close to me',
    'stores near',
    'store near',
    'stores near me',
    'store near me',
    'near my location',
    'in my area',
    'near here',
    'close by',
    'close around',
    'near my place',
    'near my home',
    'near my current location',
    'find stores near me',
    'stores around',
    'store around me',
    'store nearby',
    'stores nearby',
    'closest store',
    'closest stores',
    'any store near me',
    'search nearby',
    'location',
    'locations',
    'my location',
    'current location',
    'where am i',
    'find near',
    'show nearby',
    'nearby stores',
    'nearby store',
    'nearby locations',
    'nearby location',
    'stores',
    'store'
  ];
  
  // Use fuzzy matching which handles:
  // 1. Exact matches (fast path)
  // 2. Spelling mistakes (e.g., "ner me" -> "near me")
  // 3. Concatenated words (e.g., "NearBuy" -> "nearbuy" matching "nearby")
  // 4. Typos and variations
  
  // First, check if any location keyword appears (exact or fuzzy)
  const result = fuzzyMatchAny(normalizedText, locationKeywords, threshold);
  if (result.match) {
    return true;
  }
  
  // Additional check: if text contains location-related words even with typos
  // Split text into words and check each word against location keywords
  const textWords = normalizedText.split(/\s+/).filter(w => w.length > 0);
  
  // Check if any word in the text matches any location keyword
  let locationWordMatches = 0;
  for (const word of textWords) {
    for (const keyword of locationKeywords) {
      // For single-word keywords, check direct similarity
      if (keyword.split(/\s+/).length === 1) {
        const similarity = similarityRatio(word, keyword);
        if (similarity >= threshold) {
          locationWordMatches++;
          break; // Found a match for this word, move to next word
        }
      }
    }
  }
  
  // If at least one location-related word is found (even with typos), consider it a location query
  // This handles cases like "Nearbuy locaitons" where both words have typos
  if (locationWordMatches > 0) {
    return true;
  }
  
  return false;
}
